<template>
    <div class="phone-verification-container">
        <div class="divider"></div>

        <div class="verification-container">
            <div class="verification-header">
                <p class="verification-title">Verification Code</p>
            </div>
            <div class="otp-container">
                <!-- input otp -->
                <prime-input-otp v-model="otp" :length="6" @keydown.enter="onEnter" @paste="onPaste" />
            </div>
            <div class="resend-container">
                <p class="resend-text">
                    Didn't get a code?
                    <span
                        :class="{
                            'resend-link': true,
                            'resend-link-active': !isSmsResent,
                            'resend-link-disabled': isSmsResent,
                        }"
                        @click="isSmsResent = true"
                    >
                        Click to resend</span
                    >.
                </p>
            </div>
            <div class="divider my-3" v-if="isSmsResent || isInvalidCode"></div>
            <div class="status-container" v-if="isSmsResent">
                <CheckCircleIcon class="status-icon success" />
                <p class="status-text success">Verification code re-sent!</p>
            </div>
            <div class="status-container" v-if="isInvalidCode">
                <ExclamationCircleIcon class="status-icon error" />
                <p class="status-text error">Invalid verification code</p>
            </div>
        </div>
    </div>

    <div class="footer-container">
        <div class="button-container">
            <PrimeButton label="Cancel" @click="closeDialog" variant="secondary" class="p-btn-root_secondary"/>

            <PrimeButton @click="verifyPhone" :loading="isVerifyingOtp">
                     {{ isVerifyingOtp ? 'Verifying...' : 'Verify & Add' }}
            </PrimeButton>
        </div>
    </div>
</template>
<script setup lang="ts">
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/vue/24/solid';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { useVaultStore } from '~/stores/person/vault/vault';
import { ModalNames } from '~/types/modals';

const isSmsResent = ref(false);
const isInvalidCode = ref(false);
const otp = ref('');

//emit
const emit = defineEmits(['closeDialog']);

//inject dialogRef
const dialogRef =   getDialogRef(ModalNames.PHONE_VERIFICATION);

//close dialog
const closeDialog = () => {
    emit('closeDialog');
};

const personStore = usePersonStore();
const vaultStore = useVaultStore();
const isVerifyingOtp = ref(false);

//verify phone
const verifyPhone = async () => {
    try {
        isInvalidCode.value = false;
        isVerifyingOtp.value = true;
        await personStore.verifyPhoneOtp({
            country_code: dialogRef.value.data.country_code.replace('+', ''),
            phone_number: dialogRef.value.data.phone_number,
            code: otp.value,
        });
        
        // If this came from an exposure, suppress the original exposure
        if (dialogRef.value.data.fromExposure && dialogRef.value.data.exposureData) {
            const exposureData = dialogRef.value.data.exposureData;
            const exposureType = exposureData.type || 'phone';
            await vaultStore.updateExposure(true, exposureType, exposureData.value);
            await vaultStore.updatePotentialExposures(exposureType, exposureData.value, true);
            
            // Refresh the potential exposures to update the UI
            await vaultStore.fetchPotentialExposures();
        } else if (dialogRef.value.data.fromExposure) {
            // Fallback for existing logic if no exposure data
            await vaultStore.updateExposure(true, 'phone', dialogRef.value.data.phone_number);
            await vaultStore.updatePotentialExposures('phone', dialogRef.value.data.phone_number, true);
        }
        
        isVerifyingOtp.value = false;
        closeDialog();
    } catch (error) {
        isVerifyingOtp.value = false;
        isInvalidCode.value = true;
        return;
    }
};

const onEnter = (e: KeyboardEvent) => {
    if (!isVerifyingOtp.value) {
        verifyPhone();
    }
};

const onPaste = (event: ClipboardEvent) => {
    setTimeout(() => {
        verifyPhone();
    }, 0);
};
</script>
<style lang="scss" scoped>
.phone-verification-container {
    @apply flex flex-col items-start gap-1.5 pt-4 self-stretch;
}

.divider {
    @apply h-px self-stretch bg-zinc-300;
}

.resend-container {
    @apply flex items-start;
}

.resend-text {
    @apply text-zinc-500 font-inter text-sm font-normal leading-5;
}

.resend-link {
    @apply underline cursor-pointer;
}

.resend-link-active {
    @apply text-blue-500;
}

.resend-link-disabled {
    @apply text-zinc-300;
}

.verification-container {
    @apply w-full;
}

.verification-header {
    @apply flex flex-col items-start gap-1.5 self-stretch pt-3;
}

.verification-title {
    @apply text-gray-700 text-sm font-medium leading-5 font-inter;
}

.otp-container {
    @apply flex items-center gap-2 h-16;
}

.status-container {
    @apply flex items-center gap-2 self-stretch;
}

.status-icon {
    @apply w-5 h-5;
}

.status-icon.success {
    @apply text-green-500;
}

.status-icon.error {
    @apply text-red-500;
}

.status-text {
    @apply text-sm font-normal leading-5 font-inter;
}

.status-text.success {
    @apply text-gray-600;
}

.status-text.error {
    @apply text-red-500;
}

//footer
.footer-container {
    @apply flex flex-col justify-center items-end self-stretch pt-8;
}

.button-container {
    @apply flex justify-end items-center gap-3 flex-1 pb-6;
}
</style>
